import type { CSSProperties } from 'react';

/**
 * `<Skeleton>` — the shared shimmer-block placeholder primitive. Renders a
 * `k-skeleton` element (styled by `@kilat/ui/premium-motion.css`, which the
 * consuming app must import once). Purely decorative: always `aria-hidden` +
 * `role="presentation"` so screen readers skip it.
 *
 * The shimmer colours are themable per-tenant via the `--k-skeleton-base` /
 * `--k-skeleton-accent` CSS vars declared in the stylesheet.
 */
export interface SkeletonProps {
  /** Block width. A number is treated as px; a string passes through (`'60%'`, `'8rem'`). */
  readonly width?: number | string;
  /** Block height. A number is treated as px; a string passes through. */
  readonly height?: number | string;
  /** Corner radius override. Defaults to the kit's `--radius-md`; ignored when `circle`. */
  readonly radius?: number | string;
  /** Pill/circle block (radius 999px). If only one dimension is given, it's squared. */
  readonly circle?: boolean;
  /** How many stacked blocks to render, separated by a small gap. Defaults to 1. */
  readonly count?: number;
  readonly className?: string;
  readonly style?: CSSProperties;
}

export function Skeleton({
  width,
  height,
  radius,
  circle = false,
  count = 1,
  className,
  style,
}: SkeletonProps) {
  // A circle with only one dimension supplied squares itself off that dimension.
  const resolvedWidth = circle && width === undefined ? height : width;
  const resolvedHeight = circle && height === undefined ? width : height;
  const resolvedRadius = circle ? 999 : radius;

  const blockStyle: CSSProperties = {
    width: resolvedWidth,
    height: resolvedHeight,
    borderRadius: resolvedRadius,
    // Caller style merges last so it always wins.
    ...style,
  };
  const blockClass = className ? `k-skeleton ${className}` : 'k-skeleton';
  const blocks = Math.max(1, count);

  if (blocks === 1) {
    return <span aria-hidden role="presentation" className={blockClass} style={blockStyle} />;
  }

  return (
    <span aria-hidden role="presentation" style={{ display: 'grid', gap: 8 }}>
      {Array.from({ length: blocks }, (_, i) => (
        <span key={i} className={blockClass} style={blockStyle} />
      ))}
    </span>
  );
}

/**
 * `<SkeletonText>` — a text-placeholder loader: `lines` short shimmer bars
 * with the last one ~60% width so it reads like a wrapped paragraph.
 */
export function SkeletonText({ lines = 3 }: { readonly lines?: number }) {
  const count = Math.max(1, lines);
  return (
    <span aria-hidden role="presentation" style={{ display: 'grid', gap: 8 }}>
      {Array.from({ length: count }, (_, i) => (
        <Skeleton key={i} height={12} width={i === count - 1 ? '60%' : '100%'} />
      ))}
    </span>
  );
}
